/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import type { AdminCustomerInfo } from '../permissions'
import { Field, showDate, showText } from '../user-detail/fields'

interface Props {
  customer: AdminCustomerInfo
  userCount: number
  /** Null when the company has no saved profile — its users keep their own permissions. */
  profileUpdatedAt: string | null
}

/** The customer row as it stands, plus how many Insights users belong to it. */
export const CompanyInfoCard: React.FC<Props> = ({ customer, userCount, profileUpdatedAt }) => (
  <section className="card bg-base-100 shadow">
    <div className="card-body">
      <h2 className="card-title text-base">Company</h2>
      <dl className="grid grid-cols-2 gap-4">
        <Field label="Customer id">
          <span className="font-mono text-xs">{customer.id}</span>
        </Field>
        <Field label="Name">{showText(customer.name)}</Field>
        <Field label="Address">
          {showText(customer.address1)}
          {customer.address2 && <span className="block">{customer.address2}</span>}
        </Field>
        <Field label="City">{showText(customer.addressCity)}</Field>
        <Field label="State">{showText(customer.addressState)}</Field>
        <Field label="Postcode">{showText(customer.addressPostcode)}</Field>
        <Field label="Country">{showText(customer.addressCountry)}</Field>
        <Field label="Started">{showDate(customer.startedAt)}</Field>
        <Field label="Ended">{showDate(customer.endedAt)}</Field>
        <Field label="Users">{userCount}</Field>
        <Field label="Profile updated">
          {profileUpdatedAt ? (
            showDate(profileUpdatedAt)
          ) : (
            <span data-testid="no-profile">No profile saved — users keep their own permissions</span>
          )}
        </Field>
      </dl>
    </div>
  </section>
)

export default CompanyInfoCard
